/**
 * Inactivity Tracker Service
 * Monitors user activity and auto-logs out non-admin users after 30 minutes of inactivity.
 * Admin users are exempt from inactivity timeout.
 */

import { BACKEND_URL } from '@/lib/api';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_BEFORE_LOGOUT = 2 * 60 * 1000; // Show warning 2 minutes before logout
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // Send heartbeat every 5 minutes

class InactivityTracker {
  constructor() {
    this.timeout = null;
    this.warningTimeout = null;
    this.heartbeatInterval = null;
    this.lastActivity = Date.now();
    this.isAdmin = false;
    this.onLogout = null;
    this.onWarning = null;
    this.isInitialized = false;
    this.boundResetTimer = this.resetTimer.bind(this);
  }

  /**
   * Initialize the tracker for a user
   * @param {Object} user - The logged-in user object
   * @param {Function} onLogout - Callback when auto-logout occurs
   * @param {Function} onWarning - Callback to show warning before logout
   */
  init(user, onLogout, onWarning) {
    if (this.isInitialized) {
      this.destroy();
    }

    this.isAdmin = user?.role === 'admin';
    this.onLogout = onLogout;
    this.onWarning = onWarning;
    this.isInitialized = true;

    // Admin users don't get inactivity timeout
    if (this.isAdmin) {
      console.log('[InactivityTracker] Admin user detected - inactivity timeout disabled');
      // Still send heartbeats to track online status
      this.startHeartbeat();
      return;
    }

    console.log('[InactivityTracker] Starting 30-minute inactivity tracker');

    // Listen for user activity events
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    events.forEach(event => {
      document.addEventListener(event, this.boundResetTimer, { passive: true });
    });

    // Start the timer
    this.resetTimer();
    
    // Start heartbeat
    this.startHeartbeat();
  }

  /**
   * Reset the inactivity timer on user activity
   */
  resetTimer() {
    // Admin users don't need timer reset
    if (this.isAdmin) return;

    this.lastActivity = Date.now();

    // Clear existing timeouts
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }

    // Set warning timeout (2 minutes before logout)
    this.warningTimeout = setTimeout(() => {
      if (this.onWarning) {
        const remainingTime = Math.ceil(WARNING_BEFORE_LOGOUT / 1000 / 60);
        this.onWarning(remainingTime);
      }
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);

    // Set logout timeout
    this.timeout = setTimeout(() => {
      this.handleInactivityLogout();
    }, INACTIVITY_TIMEOUT);
  }

  /**
   * Handle auto-logout due to inactivity
   */
  async handleInactivityLogout() {
    console.log('[InactivityTracker] Inactivity timeout reached - logging out');

    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Call backend to record auto-logout
        await fetch(`${BACKEND_URL}/api/auth/auto-logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('[InactivityTracker] Error recording auto-logout:', error);
    }

    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivity');

    // Call the logout callback
    if (this.onLogout) {
      this.onLogout('inactivity');
    }
  }

  /**
   * Start sending periodic heartbeats to track online status
   */
  startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    const sendHeartbeat = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await fetch(`${BACKEND_URL}/api/auth/heartbeat`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        }
      } catch (error) {
        console.error('[InactivityTracker] Heartbeat failed:', error);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Then send periodically
    this.heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
  }

  /**
   * Get remaining time before auto-logout (in seconds)
   */
  getRemainingTime() {
    if (this.isAdmin) return Infinity;
    
    const elapsed = Date.now() - this.lastActivity;
    const remaining = Math.max(0, INACTIVITY_TIMEOUT - elapsed);
    return Math.ceil(remaining / 1000);
  }

  /**
   * Check if user is close to being logged out
   */
  isWarningPeriod() {
    if (this.isAdmin) return false;
    return this.getRemainingTime() <= WARNING_BEFORE_LOGOUT / 1000;
  }

  /**
   * Clean up event listeners and timers
   */
  destroy() {
    console.log('[InactivityTracker] Destroying tracker');

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    events.forEach(event => {
      document.removeEventListener(event, this.boundResetTimer);
    });

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    this.isInitialized = false;
    this.onLogout = null;
    this.onWarning = null;
  }
}

// Export singleton instance
export const inactivityTracker = new InactivityTracker();
